import { type InferType, object, ref, string, type StringSchema } from "yup";
import { isUSA } from "../../../../../common/utils";
import { AddressInputSchemaGenerator } from "../../../../SharedAddressInput/constants";
import { getNameFromCountry } from "../../../mappers";

export enum AccountDetailsInputFields {
  AccountHolderName = "accountHolderName", // also refers to beneficiary name
  AccountNumber = "accountNumber",
  ConfirmAccountNumber = "confirmAccountNumber",
  ReferenceToBeneficiary = "referenceToBeneficiary",
  OBI = "obi",
  BBI = "bbi",
  BeneficiaryAddressCountry = "country",
  BeneficiaryAddressZip = "zip",
  BeneficiaryAddressProvince = "province",
  BeneficiaryAddressState = "state",
  BeneficiaryAddressCity = "city",
  BeneficiaryAddressAddressLine1 = "streetLine1",
  BeneficiaryAddressAddressLine2 = "streetLine2",
}

export enum WireExtraFieldInputFields {
  Field0 = "field0",
  Field1 = "field1",
  Field2 = "field2",
  Field3 = "field3",
}

const WireExtraFieldObject = (fieldSchema: StringSchema) =>
  object().shape({
    [WireExtraFieldInputFields.Field0]: fieldSchema.max(
      35,
      "Field must be 35 characters or less",
    ),
    [WireExtraFieldInputFields.Field1]: fieldSchema.max(
      35,
      "Field must be 35 characters or less",
    ),
    [WireExtraFieldInputFields.Field2]: fieldSchema.max(
      35,
      "Field must be 35 characters or less",
    ),
    [WireExtraFieldInputFields.Field3]: fieldSchema.max(
      35,
      "Field must be 35 characters or less",
    ),
  });

export const AccountDetailsInputSchema = object()
  .shape({
    [AccountDetailsInputFields.AccountHolderName]: string()
      .max(35, "Account name must be 35 characters or less")
      .matches(/\S/, "Name cannot be just spaces")
      .required("Required field"),
    [AccountDetailsInputFields.AccountNumber]: string()
      .max(34, "Account number must be 34 characters or less")
      .matches(
        /^[-.a-zA-Z0-9]+$/,
        "The account number may contain only alphanumeric characters, dashes and periods",
      )
      .required("Required field"),
    [AccountDetailsInputFields.ConfirmAccountNumber]: string()
      .oneOf([ref("accountNumber")], "Must match account number")
      .required("Required field"),
    [AccountDetailsInputFields.ReferenceToBeneficiary]: string().max(
      16,
      "Reference to beneficiary must be 16 characters or less",
    ),
    [AccountDetailsInputFields.OBI]: WireExtraFieldObject(string()),
    [AccountDetailsInputFields.BBI]: WireExtraFieldObject(string()),
  })
  .concat(
    AddressInputSchemaGenerator({
      testIsUSA: (country) => isUSA(getNameFromCountry(country)),
    }),
  );

export type AccountDetailsInputType = InferType<typeof AccountDetailsInputSchema>;
